import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Settings, Plus, X, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import {
  fetchDevices,
  fetchPermissionsMap,
  userDevicesFromMap,
  linkDevice,
  unlinkDevice,
  type User,
} from '@/lib/traccar-api';

interface EditUserDevicesDialogProps {
  user: User;
  trigger?: React.ReactNode;
}

export function EditUserDevicesDialog({ user, trigger }: EditUserDevicesDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [addAllProgress, setAddAllProgress] = useState<{ done: number; total: number } | null>(null);

  const { data: allDevices = [], isLoading: loadingAll } = useQuery({
    queryKey: ['devices'],
    queryFn: fetchDevices,
    enabled: open,
  });

  const { data: permsMap } = useQuery({
    queryKey: ['permissions-map'],
    queryFn: fetchPermissionsMap,
    enabled: open,
  });

  const userDevices = permsMap ? userDevicesFromMap(user.id, allDevices, permsMap) : [];
  const loadingUser = !permsMap;

  const userDeviceIds = new Set(userDevices.map(d => d.id));

  const linkMutation = useMutation({
    mutationFn: (deviceId: number) => linkDevice(user.id, deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions-map'] });
      toast.success(t('users.deviceLinked'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('users.deviceLinkError'));
    },
  });

  const handleAddAll = async () => {
    const devices = [...availableDevices];
    setAddAllProgress({ done: 0, total: devices.length });
    let succeeded = 0;
    let failed = 0;
    for (const device of devices) {
      try {
        await linkDevice(user.id, device.id);
        succeeded++;
      } catch {
        failed++;
      }
      setAddAllProgress({ done: succeeded + failed, total: devices.length });
    }
    setAddAllProgress(null);
    queryClient.invalidateQueries({ queryKey: ['permissions-map'] });
    if (failed === 0) {
      toast.success(t('users.allDevicesLinked', { count: succeeded }));
    } else {
      toast.warning(t('users.allDevicesLinkedPartial', { succeeded, failed }));
    }
  };

  const unlinkMutation = useMutation({
    mutationFn: (deviceId: number) => unlinkDevice(user.id, deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions-map'] });
      toast.success(t('users.deviceUnlinked'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('users.deviceUnlinkError'));
    },
  });

  const availableDevices = allDevices.filter(
    d => !userDeviceIds.has(d.id) && d.name.toLowerCase().includes(search.toLowerCase())
  );

  const isLoading = loadingAll || loadingUser;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="icon" className="h-7 w-7">
            <Settings className="h-3.5 w-3.5" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('users.editDevices')}</DialogTitle>
          <DialogDescription>
            {t('users.editDevicesDescription', { name: user.name })}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Associated devices */}
            <div>
              <h4 className="text-sm font-medium mb-2">{t('users.associatedDevices')}</h4>
              {userDevices.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">{t('users.noDevicesAssociated')}</p>
              ) : (
                <ScrollArea className="h-[120px]">
                  <div className="flex flex-wrap gap-2">
                    {userDevices.map(device => (
                      <Badge
                        key={device.id}
                        variant="secondary"
                        className="gap-1 pr-1"
                      >
                        {device.name}
                        <button
                          onClick={() => unlinkMutation.mutate(device.id)}
                          disabled={unlinkMutation.isPending}
                          className="ml-1 rounded-full p-0.5 hover:bg-destructive/20 hover:text-destructive transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>

            {/* Available devices */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium">{t('users.availableDevices')}</h4>
                {availableDevices.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 text-xs"
                    onClick={handleAddAll}
                    disabled={!!addAllProgress || linkMutation.isPending}
                  >
                    {addAllProgress ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        {addAllProgress.done}/{addAllProgress.total}
                      </>
                    ) : (
                      <>
                        <PlusCircle className="h-3.5 w-3.5" />
                        {t('users.addAll')}
                      </>
                    )}
                  </Button>
                )}
              </div>
              <Input
                placeholder={t('users.searchDevices')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mb-2"
              />
              <ScrollArea className="h-[160px] border rounded-md">
                {availableDevices.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-3">
                    {search ? t('users.noDevicesMatch') : t('users.allDevicesAssigned')}
                  </p>
                ) : (
                  <div className="p-2 space-y-1">
                    {availableDevices.map(device => (
                      <div
                        key={device.id}
                        className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50"
                      >
                        <span className="text-sm">{device.name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => linkMutation.mutate(device.id)}
                          disabled={linkMutation.isPending}
                          className="h-7 gap-1"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          {t('common.add')}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
